import {
  Button,
  LoadingIndicator,
  SlideoutMenu,
  Tabs,
} from "@light-technology/components";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { AlertCircle, Check, Link01 } from "@untitledui/icons";
import { type ReactNode, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api } from "../../state/api";
import type { AppResponse } from "../../state/api.types";
import {
  findProgramDevice,
  getNeedsAttention,
  getProgramDevices,
} from "../../utilities/devices";
import { PRESERVE_SCROLL } from "../../utilities/links";
import { AccountSlideoutContracts } from "./AccountSlideoutContracts";
import { AccountSlideoutDetails } from "./AccountSlideoutDetails";
import { AccountSlideoutDevices } from "./AccountSlideoutDevices";
import { AccountSlideoutInvoices } from "./AccountSlideoutInvoices";

type AccountSlideoutProps = {
  app: AppResponse;
  children: ReactNode | ReactNode[];
};

const DeviceCountIcon = ({
  count,
  error,
}: {
  count: number;
  error: boolean;
}) => {
  return (
    <div className="relative rounded-full border border-secondary pr-sm pl-sm">
      <div className="text-xs">{count}</div>
      {error && (
        <AlertCircle className="absolute -right-1 -bottom-1 size-3 fill-utility-warning-200 stroke-utility-warning-700" />
      )}
    </div>
  );
};

export const AccountSlideout = (props: AccountSlideoutProps) => {
  const navigate = useNavigate();
  const { accountId } = useParams();

  const getAccount = api.useGetAccountQuery(
    accountId
      ? {
          accountUUID: accountId,
          appUUID: props.app.uuid,
        }
      : skipToken,
  );

  const getDevices = api.useGetAccountDevicesQuery(
    accountId
      ? {
          accountUUID: accountId,
          appUUID: props.app.uuid,
        }
      : skipToken,
  );

  const getPrograms = api.useGetAccountProgramAgreementsQuery(
    accountId
      ? {
          accountUUID: accountId,
          appUUID: props.app.uuid,
        }
      : skipToken,
  );

  const programs = getPrograms.data?.data.map((p) => p.program) ?? [];
  const devices = getDevices.data?.data ?? [];
  const devicesByProgram = getProgramDevices({ devices, programs });
  const count = devicesByProgram.size;
  let deviceError = false;
  for (const program of devicesByProgram.keys()) {
    const device = findProgramDevice(devicesByProgram.get(program) ?? []);
    if (device && getNeedsAttention(device)) {
      deviceError = true;
      break;
    }
  }
  const tabs = [
    { id: "details", label: "Details" },
    {
      id: "devices",
      label: (
        <div className="flex flex-row gap-xs">
          Devices
          {count > 0 && <DeviceCountIcon count={count} error={deviceError} />}
        </div>
      ),
    },
    { id: "invoices", label: "Invoices" },
    { id: "contracts", label: "Contracts" },
  ];

  const [tab, setTab] = useState<string | number>(tabs[0].id);

  const close = () => {
    navigate(`/apps/${props.app.slug}/accounts`, PRESERVE_SCROLL);
  };

  return (
    <SlideoutMenu.Trigger
      isOpen={!!accountId}
      onOpenChange={(isOpen) => {
        if (isOpen) {
          setTab(tabs[0].id);
        } else {
          close();
        }
      }}
    >
      {props.children}
      <SlideoutMenu isDismissable className="max-w-xs">
        <Tabs selectedKey={tab} onSelectionChange={setTab}>
          <SlideoutMenu.Header onClose={close} tabs={tabs}>
            {!getAccount.data ? (
              <LoadingIndicator type="brand-spinner" />
            ) : (
              <>
                <div className="text-display-xs font-semibold">
                  {[getAccount.data.first_name, getAccount.data.last_name]
                    .filter(Boolean)
                    .join(" ") || getAccount.data.email}
                  <Button
                    size="sm"
                    color="tertiary"
                    tooltip="Copy shareable link to account"
                    tooltipOnClick="Link copied to clipboard"
                    iconLeading={Link01}
                    iconLeadingOnClick={Check}
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                    }}
                  />
                </div>
                <div className="font-mono text-sm text-tertiary">
                  {getAccount.data.uuid}
                </div>
              </>
            )}
          </SlideoutMenu.Header>
          {getAccount.data && (
            <SlideoutMenu.Content className="pt-3xl">
              <Tabs.Panel id="details">
                <AccountSlideoutDetails account={getAccount.data} />
              </Tabs.Panel>
              <Tabs.Panel id="devices">
                <AccountSlideoutDevices
                  account={getAccount.data}
                  app={props.app}
                />
              </Tabs.Panel>
              <Tabs.Panel id="invoices">
                <AccountSlideoutInvoices
                  account={getAccount.data}
                  app={props.app}
                />
              </Tabs.Panel>
              <Tabs.Panel id="contracts">
                <AccountSlideoutContracts
                  key={getAccount.data.uuid}
                  account={getAccount.data}
                  app={props.app}
                />
              </Tabs.Panel>
            </SlideoutMenu.Content>
          )}
        </Tabs>
      </SlideoutMenu>
    </SlideoutMenu.Trigger>
  );
};
